// SignUpForm.js
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { api } from '../../api';

const SignUpForm = ({ closeModal }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: ''
    });
    const [errors, setErrors] = useState({});

    const validateEthiopianPhoneNumber = (phone) => {
        const patterns = [
            /^(\+251|0)(9|7)[0-9]{8}$/, // Mobile
            /^(\+251|0)(11|12|13|14|15|16|17|18|19|22|23|24|25|33|34|35|36|41|44|46|47|51|54|55|56|57|58|59|61|62|65|67|68|71|72|73|74|75|76|77|79|81|82|83|84|85|86|87|88|89|91|92|93|94|95|96|97|98|99)[0-9]{6}$/
        ];
        const cleanedPhone = phone.replace(/[^\d+]/g, '');
        return patterns.some(pattern => pattern.test(cleanedPhone));
    };

    const formatPhoneNumber = (phone) => {
        let cleaned = phone.replace(/[^\d+]/g, '');
        if (cleaned.startsWith('0')) {
            cleaned = '+251' + cleaned.substring(1);
        } else if (cleaned.startsWith('251')) {
            cleaned = '+' + cleaned;
        }
        return cleaned;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailPattern.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!validateEthiopianPhoneNumber(formData.phoneNumber)) {
            newErrors.phoneNumber = 'Please enter a valid Ethiopian phone number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const subscriberData = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                phone_number: formatPhoneNumber(formData.phoneNumber),
            };

            const response = await api.post("/subscribers", subscriberData);
            
            Swal.fire({
                title: 'Thank You!',
                text: 'You have successfully signed up for our newsletter.',
                icon: 'success',
                confirmButtonText: 'Great!',
                confirmButtonColor: '#FD5F20',
            });

            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: ''
            });
                        closeModal(); // Close modal after success

        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to sign up. Please try again later.',
                icon: 'error',
                confirmButtonText: 'Okay',
                confirmButtonColor: '#FD5F20',
            });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-full p-4 mt-4 rounded-lg">
            <div className="flex flex-col md:flex-row gap-8">
                <div className="mb-2 w-full">
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full px-2 py-2 border-2 ${errors.firstName ? 'border-red-500' : 'border-[#FD5F20]'} rounded-lg mt-2 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] placeholder-gray-500`}
                        placeholder="Enter your first name"
                        aria-label="First Name"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div className="mb-2 w-full">
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full px-2 py-2 border-2 ${errors.lastName ? 'border-red-500' : 'border-[#FD5F20]'} rounded-lg mt-2 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] placeholder-gray-500`}
                        placeholder="Enter your last name"
                        aria-label="Last Name"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
            </div>
            <div className="flex flex-col md:flex-row gap-8">
                <div className="mb-2 w-full">
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-2 py-2 border-2 ${errors.email ? 'border-red-500' : 'border-[#FD5F20]'} rounded-lg mt-2 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] placeholder-gray-500`}
                        placeholder="Enter your email"
                        aria-label="Email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div className="mb-2 w-full">
                    <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className={`w-full px-2 py-2 border-2 ${errors.phoneNumber ? 'border-red-500' : 'border-[#FD5F20]'} rounded-lg mt-2 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] placeholder-gray-500`}
                        placeholder="Enter your phone number (e.g., +251911223344 or 0911223344)"
                        aria-label="Phone Number"
                    />
                    {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
                    <p className="text-xs text-gray-500 mt-1">Format: +251XXXXXXXXX or 0XXXXXXXXX</p>
                </div>
            </div>
            <div className="mb-2">
                <button
                    type="submit"
                    className="w-full px-2 py-2 bg-[#FD5F20] opacity-90 text-white rounded-lg hover:bg-[#E6551C] focus:outline-none focus:ring-2 focus:ring-[#FF6A33]"
                >
                    Sign Up
                </button>
            </div>
        </form>
    );
};

export default SignUpForm;